import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

@Injectable({
    providedIn: 'root'
})
export class PwaInstallService {
    private deferredPrompt: BeforeInstallPromptEvent | null = null;
    private installabilitySubject = new BehaviorSubject<boolean>(false);

    public installabilityChanged$: Observable<boolean> = this.installabilitySubject.asObservable();

    constructor() {
        this.setupInstallPromptListener();
        this.checkInitialInstallability();
    }

    /**
     * Configura il listener per l'evento beforeinstallprompt
     */
    private setupInstallPromptListener(): void {
        window.addEventListener('beforeinstallprompt', (e: Event) => {
            console.log('PWA: beforeinstallprompt event fired');

            // Previene il prompt automatico del browser
            e.preventDefault();

            // Salva l'evento per uso successivo
            this.deferredPrompt = e as BeforeInstallPromptEvent;

            // Notifica che l'app è installabile
            this.installabilitySubject.next(true);
        });

        // Listener per quando l'app viene installata
        window.addEventListener('appinstalled', () => {
            console.log('PWA: App installata con successo');
            this.deferredPrompt = null;
            this.installabilitySubject.next(false);

            // Pulisce le preferenze del banner dato che l'app è installata
            this.clearBannerPreferences();
        });
    }

    /**
     * Controlla l'installabilità iniziale
     */
    private checkInitialInstallability(): void {
        // Controlla se l'app è già installata
        if (this.isStandalone()) {
            this.installabilitySubject.next(false);
            return;
        }

        // Per iOS, considera sempre installabile (richiede istruzioni manuali)
        if (this.isIOS() && !this.isStandalone()) {
            this.installabilitySubject.next(true);
            return;
        }

        // Per altri browser, dipende dall'evento beforeinstallprompt
        this.installabilitySubject.next(!!this.deferredPrompt);
    }

    /**
     * Mostra il prompt di installazione
     */
    async promptInstall(): Promise<boolean> {
        if (!this.deferredPrompt) {
            console.warn('PWA: Nessun prompt di installazione disponibile');
            return false;
        }

        try {
            // Mostra il prompt di installazione
            await this.deferredPrompt.prompt();

            // Aspetta la scelta dell'utente
            const choiceResult = await this.deferredPrompt.userChoice;

            console.log('PWA: Scelta utente:', choiceResult.outcome);

            if (choiceResult.outcome === 'accepted') {
                console.log('PWA: Utente ha accettato l\'installazione');
                this.deferredPrompt = null;
                this.installabilitySubject.next(false);
                return true;
            } else {
                console.log('PWA: Utente ha rifiutato l\'installazione');
                return false;
            }
        } catch (error) {
            console.error('PWA: Errore durante il prompt di installazione:', error);
            return false;
        }
    }

    /**
     * Verifica se l'app è installabile
     */
    isInstallable(): boolean {
        return !!this.deferredPrompt || (this.isIOS() && !this.isStandalone());
    }

    /**
     * Verifica se l'app è in modalità standalone (installata)
     */
    isStandalone(): boolean {
        return window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true ||
            document.referrer.includes('android-app://');
    }

    /**
     * Verifica se il dispositivo è iOS
     */
    isIOS(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    }

    /**
     * Verifica se il dispositivo è Android
     */
    isAndroid(): boolean {
        return /Android/.test(navigator.userAgent);
    }

    /**
     * Ottiene il nome del browser
     */
    getBrowserName(): string {
        const userAgent = navigator.userAgent;

        if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
            return 'Chrome';
        } else if (userAgent.includes('Firefox')) {
            return 'Firefox';
        } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
            return 'Safari';
        } else if (userAgent.includes('Edg')) {
            return 'Edge';
        } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
            return 'Opera';
        } else {
            return 'Browser sconosciuto';
        }
    }

    /**
     * Verifica se il dispositivo è mobile
     */
    isMobile(): boolean {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    /**
     * Verifica se il banner dovrebbe essere mostrato
     * Il banner si mostra SEMPRE su mobile, a meno che l'app non sia già installata
     */
    shouldShowBanner(): boolean {
        // Non mostrare se non è mobile
        if (!this.isMobile()) return false;
        // Non mostrare se l'app è già installata (standalone)
        if (this.isStandalone()) return false;
        // Non mostrare se è stato chiuso in questa sessione
        if (sessionStorage.getItem('pwa_banner_closed') === 'true') return false;
        // Mostra sempre su mobile se installabile
        return this.isInstallable();
    }

    /**
     * Nasconde il banner solo per questa sessione
     * Al prossimo accesso il banner riapparirà
     */
    dismissBannerForSession(): void {
        sessionStorage.setItem('pwa_banner_closed', 'true');
        console.log('PWA: Banner chiuso per questa sessione');
    }

    /**
     * Pulisce le preferenze del banner
     */
    private clearBannerPreferences(): void {
        sessionStorage.removeItem('pwa_banner_closed');
    }

    /**
     * Resetta le preferenze del banner (per testing o reset manuale)
     */
    resetBannerPreferences(): void {
        this.clearBannerPreferences();
        console.log('PWA: Preferenze banner resettate');
    }

    /**
     * Ottiene informazioni sullo stato dell'installazione
     */
    getInstallationInfo(): {
        isInstallable: boolean;
        isStandalone: boolean;
        isIOS: boolean;
        isAndroid: boolean;
        browserName: string;
        canShowBanner: boolean;
    } {
        return {
            isInstallable: this.isInstallable(),
            isStandalone: this.isStandalone(),
            isIOS: this.isIOS(),
            isAndroid: this.isAndroid(),
            browserName: this.getBrowserName(),
            canShowBanner: this.shouldShowBanner()
        };
    }
}